/**
 * 前端加密工具类
 * 支持AES对称加密和RSA非对称加密
 * 与后端CryptoUtil.java兼容
 */

// 使用 ES6 import 方式导入（webpack 会在构建时处理）
import CryptoJS from 'crypto-js'
let JSEncrypt = null

// 尝试导入 jsencrypt（用于RSA加密）
try {
  // jsencrypt 的导入方式
  const jsencryptModule = require('jsencrypt')
  // jsencrypt 直接导出构造函数，格式：function JSEncrypt()
  // 也可能导出为对象：{ JSEncrypt: function }
  if (typeof jsencryptModule === 'function') {
    // 直接是构造函数
    JSEncrypt = jsencryptModule
  } else if (jsencryptModule && jsencryptModule.JSEncrypt && typeof jsencryptModule.JSEncrypt === 'function') {
    // 对象形式：{ JSEncrypt: function }
    JSEncrypt = jsencryptModule.JSEncrypt
  } else if (jsencryptModule && jsencryptModule.default && typeof jsencryptModule.default === 'function') {
    // ES6 默认导出
    JSEncrypt = jsencryptModule.default
  } else {
    throw new Error('JSEncrypt not found in module')
  }
  
  // 验证 JSEncrypt 是否是构造函数
  if (typeof JSEncrypt !== 'function') {
    throw new Error('JSEncrypt is not a constructor, type: ' + typeof JSEncrypt)
  }
  
  console.debug('JSEncrypt 已成功加载，类型:', typeof JSEncrypt)
} catch (e) {
  try {
    // 尝试从 window 对象获取
    if (typeof window !== 'undefined' && window.JSEncrypt) {
      JSEncrypt = window.JSEncrypt
      console.debug('JSEncrypt 从 window 对象加载')
    } else {
      throw new Error('JSEncrypt not available')
    }
  } catch (e2) {
    console.warn('jsencrypt 未安装，RSA加密功能不可用。建议安装: npm install jsencrypt', e, e2)
  }
}

/**
 * 加密类型枚举
 */
export const CryptoType = {
  AES: 'AES',
  RSA: 'RSA'
}

/**
 * 默认AES密钥（应与后端保持一致）
 */
const DEFAULT_AES_KEY = '2nHtbGAYxq3wgZZdcFtye5LKYSuAmWVz'

/**
 * AES加密
 * @param {String} content - 待加密内容
 * @param {String} key - 密钥（如果为null，使用默认密钥）
 * @returns {String} 加密后的Base64字符串
 */
export function aesEncrypt(content, key = null) {
  if (!content) {
    return content
  }

  if (!CryptoJS) {
    console.error('crypto-js 未安装，无法使用AES加密')
    throw new Error('crypto-js 未安装，无法使用AES加密')
  }

  try {
    const secretKey = key || DEFAULT_AES_KEY
    const keyUtf8 = CryptoJS.enc.Utf8.parse(secretKey)

    // 使用 AES/ECB/PKCS5Padding 加密（与后端兼容）
    const encrypted = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(content),
      keyUtf8,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )

    // 转换为 Base64 字符串
    let base64 = encrypted.ciphertext.toString(CryptoJS.enc.Base64)

    // 处理特殊字符，避免URL传输问题（与后端保持一致）
    base64 = base64.replace(/\//g, '~SLASH~').replace(/\+/g, '~SUB~')

    return base64
  } catch (error) {
    console.error('AES加密失败:', error)
    throw new Error('AES加密失败: ' + error.message)
  }
}

/**
 * AES解密
 * @param {String} content - 待解密内容
 * @param {String} key - 密钥（如果为null，使用默认密钥）
 * @returns {String} 解密后的字符串
 */
export function aesDecrypt(content, key = null) {
  if (!content) {
    return content
  }

  if (!CryptoJS) {
    console.error('crypto-js 未安装，无法使用AES解密')
    throw new Error('crypto-js 未安装，无法使用AES解密')
  }

  try {
    const secretKey = key || DEFAULT_AES_KEY
    const keyUtf8 = CryptoJS.enc.Utf8.parse(secretKey)

    // 恢复特殊字符
    content = content.replace(/~SLASH~/g, '/').replace(/~SUB~/g, '+')

    // Base64解码
    const encryptedData = CryptoJS.enc.Base64.parse(content)

    // 创建加密对象
    const encrypted = CryptoJS.lib.CipherParams.create({
      ciphertext: encryptedData
    })

    // 解密
    const decrypted = CryptoJS.AES.decrypt(
      encrypted,
      keyUtf8,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )

    // 转换为UTF-8字符串
    return decrypted.toString(CryptoJS.enc.Utf8)
  } catch (error) {
    console.error('AES解密失败:', error)
    throw new Error('AES解密失败: ' + error.message)
  }
}

/**
 * RSA加密（使用公钥）
 * @param {String} content - 待加密内容
 * @param {String} publicKey - 公钥（Base64编码）
 * @returns {String} 加密后的Base64字符串
 */
export function rsaEncrypt(content, publicKey) {
  if (!content || !publicKey) {
    return content
  }

  if (!JSEncrypt) {
    console.error('jsencrypt 未安装，无法使用RSA加密')
    throw new Error('jsencrypt 未安装，无法使用RSA加密')
  }

  try {
    // JSEncrypt 本身就是构造函数
    const encrypt = new JSEncrypt()
    
    // 如果公钥不是完整的PEM格式，需要转换
    let formattedKey = publicKey
    if (!publicKey.includes('-----BEGIN PUBLIC KEY-----')) {
      formattedKey = `-----BEGIN PUBLIC KEY-----\n${publicKey}\n-----END PUBLIC KEY-----`
    }
    
    encrypt.setPublicKey(formattedKey)
    const encrypted = encrypt.encrypt(content)
    
    // RSA加密返回的是Base64字符串，可能包含URL不安全的字符（/和+）
    // 需要替换为安全字符，与后端保持一致
    if (encrypted) {
      return encrypted.replace(/\//g, '~SLASH~').replace(/\+/g, '~SUB~')
    }
    
    return encrypted
  } catch (error) {
    console.error('RSA加密失败:', error)
    throw new Error('RSA加密失败: ' + error.message)
  }
}

/**
 * RSA解密（使用私钥）
 * @param {String} content - 待解密内容
 * @param {String} privateKey - 私钥（Base64编码）
 * @returns {String} 解密后的字符串
 */
export function rsaDecrypt(content, privateKey) {
  if (!content || !privateKey) {
    return content
  }

  if (!JSEncrypt) {
    console.error('jsencrypt 未安装，无法使用RSA解密')
    throw new Error('jsencrypt 未安装，无法使用RSA解密')
  }

  try {
    // JSEncrypt 本身就是构造函数
    const encrypt = new JSEncrypt()
    
    // 如果私钥不是完整的PEM格式，需要转换
    let formattedKey = privateKey
    if (!privateKey.includes('-----BEGIN PRIVATE KEY-----') && !privateKey.includes('-----BEGIN RSA PRIVATE KEY-----')) {
      // 尝试两种格式
      formattedKey = `-----BEGIN PRIVATE KEY-----\n${privateKey}\n-----END PRIVATE KEY-----`
    }
    
    console.debug('RSA解密调试:', {
      contentLength: content ? content.length : 0,
      contentPreview: content ? content.substring(0, 50) + '...' : 'null',
      privateKeyLength: privateKey ? privateKey.length : 0,
      privateKeyPreview: privateKey ? privateKey.substring(0, 50) + '...' : 'null',
      formattedKeyLength: formattedKey ? formattedKey.length : 0,
      hasBeginMarker: formattedKey.includes('-----BEGIN'),
      hasEndMarker: formattedKey.includes('-----END')
    })
    
    const setKeyResult = encrypt.setPrivateKey(formattedKey)
    console.debug('RSA设置私钥结果:', setKeyResult)
    
    const decrypted = encrypt.decrypt(content)
    console.debug('RSA解密结果:', {
      success: decrypted !== false && decrypted !== null,
      resultType: typeof decrypted,
      resultValue: decrypted === false ? 'false' : decrypted === null ? 'null' : (decrypted ? decrypted.substring(0, 20) + '...' : 'empty')
    })
    
    // JSEncrypt.decrypt() 可能返回 false 或 null，需要检查
    if (decrypted === false || decrypted === null) {
      // 尝试使用 RSA PRIVATE KEY 格式
      if (!privateKey.includes('-----BEGIN RSA PRIVATE KEY-----')) {
        const rsaFormattedKey = `-----BEGIN RSA PRIVATE KEY-----\n${privateKey}\n-----END RSA PRIVATE KEY-----`
        console.debug('尝试使用 RSA PRIVATE KEY 格式')
        encrypt.setPrivateKey(rsaFormattedKey)
        const retryDecrypted = encrypt.decrypt(content)
        if (retryDecrypted !== false && retryDecrypted !== null) {
          return String(retryDecrypted)
        }
      }
      throw new Error('RSA解密失败：解密结果为 ' + decrypted + '，可能是私钥配置不正确或与公钥不匹配')
    }
    
    // 确保返回字符串
    return String(decrypted)
  } catch (error) {
    console.error('RSA解密失败:', error)
    console.error('RSA解密失败详情:', {
      errorMessage: error.message,
      contentLength: content ? content.length : 0,
      privateKeyLength: privateKey ? privateKey.length : 0
    })
    throw new Error('RSA解密失败: ' + error.message)
  }
}

/**
 * 根据类型加密
 * @param {String} content - 待加密内容
 * @param {String} cryptoType - 加密类型：'AES' 或 'RSA'
 * @param {String} key - 密钥（AES密钥或RSA公钥）
 * @returns {String} 加密后的字符串
 */
export function encrypt(content, cryptoType, key) {
  if (cryptoType === CryptoType.AES) {
    return aesEncrypt(content, key)
  } else if (cryptoType === CryptoType.RSA) {
    return rsaEncrypt(content, key)
  } else {
    throw new Error('不支持的加密类型: ' + cryptoType)
  }
}

/**
 * 根据类型解密
 * @param {String} content - 待解密内容
 * @param {String} cryptoType - 加密类型：'AES' 或 'RSA'
 * @param {String} key - 密钥（AES密钥或RSA私钥）
 * @returns {String} 解密后的字符串
 */
export function decrypt(content, cryptoType, key) {
  if (cryptoType === CryptoType.AES) {
    return aesDecrypt(content, key)
  } else if (cryptoType === CryptoType.RSA) {
    return rsaDecrypt(content, key)
  } else {
    throw new Error('不支持的加密类型: ' + cryptoType)
  }
}

/**
 * 生成AES密钥（16字节，Base64编码）
 * @returns {String} Base64编码的AES密钥
 */
export function generateAESKey() {
  if (!CryptoJS) {
    console.error('crypto-js 未安装，无法生成AES密钥')
    throw new Error('crypto-js 未安装，无法生成AES密钥')
  }
  
  try {
    // 生成16字节（128位）的随机密钥
    const key = CryptoJS.lib.WordArray.random(16)
    // 转换为Base64字符串
    return CryptoJS.enc.Base64.stringify(key)
  } catch (error) {
    console.error('生成AES密钥失败:', error)
    throw new Error('生成AES密钥失败: ' + error.message)
  }
}

/**
 * 混合加密：使用RSA公钥加密AES密钥，使用AES密钥加密数据
 * 数据格式：{"encryptedKey":"RSA加密的AES密钥","encryptedData":"AES加密的数据"}
 * 
 * @param {String} content - 待加密内容
 * @param {String} rsaPublicKey - RSA公钥（Base64编码）
 * @returns {String} JSON格式的加密数据：{"encryptedKey":"...","encryptedData":"..."}
 */
export function hybridEncrypt(content, rsaPublicKey) {
  if (!content) {
    return content
  }

  if (!JSEncrypt) {
    console.error('jsencrypt 未安装，无法使用混合加密')
    throw new Error('jsencrypt 未安装，无法使用混合加密')
  }

  if (!CryptoJS) {
    console.error('crypto-js 未安装，无法使用混合加密')
    throw new Error('crypto-js 未安装，无法使用混合加密')
  }

  try {
    // 验证 JSEncrypt 是否是构造函数
    if (typeof JSEncrypt !== 'function') {
      console.error('JSEncrypt 不是构造函数:', typeof JSEncrypt, JSEncrypt)
      throw new Error('JSEncrypt 不是构造函数，类型: ' + typeof JSEncrypt)
    }
    
    // 第一步：生成AES密钥（16字节的随机密钥）
    const aesKeyWordArray = CryptoJS.lib.WordArray.random(16)
    // 转换为Base64字符串用于RSA加密
    const aesKeyBase64 = CryptoJS.enc.Base64.stringify(aesKeyWordArray)
    console.debug('混合加密步骤1: AES密钥(Base64)已生成，长度:', aesKeyBase64.length, '原始字节:', aesKeyWordArray.sigBytes)
    
    // 第二步：使用AES密钥加密数据（直接使用WordArray）
    const encrypted = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(content),
      aesKeyWordArray,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )
    // 转换为 Base64 字符串并处理特殊字符
    let encryptedData = encrypted.ciphertext.toString(CryptoJS.enc.Base64)
    encryptedData = encryptedData.replace(/\//g, '~SLASH~').replace(/\+/g, '~SUB~')
    console.debug('混合加密步骤2: 数据已使用AES加密，长度:', encryptedData.length)
    
    // 第三步：使用RSA公钥加密AES密钥（Base64字符串）
    const encryptedKey = rsaEncrypt(aesKeyBase64, rsaPublicKey)
    console.debug('混合加密步骤3: AES密钥已使用RSA加密，长度:', encryptedKey ? encryptedKey.length : 0)
    
    if (!encryptedKey) {
      throw new Error('RSA加密AES密钥失败，返回null')
    }
    
    // 第四步：组装JSON格式的加密数据
    const result = {
      encryptedKey: encryptedKey,
      encryptedData: encryptedData
    }
    
    const finalResult = JSON.stringify(result)
    console.debug('混合加密步骤4: 最终结果已组装，长度:', finalResult.length)
    return finalResult
  } catch (error) {
    console.error('混合加密失败:', error)
    console.error('混合加密失败详情:', {
      errorMessage: error.message,
      errorStack: error.stack,
      JSEncryptType: typeof JSEncrypt,
      CryptoJSType: typeof CryptoJS,
      rsaPublicKeyLength: rsaPublicKey ? rsaPublicKey.length : 0
    })
    throw new Error('混合加密失败: ' + error.message)
  }
}

/**
 * 混合解密：使用RSA私钥解密AES密钥，使用AES密钥解密数据
 * 数据格式：{"encryptedKey":"RSA加密的AES密钥","encryptedData":"AES加密的数据"}
 * 
 * @param {String} encryptedContent - 加密内容（JSON字符串或对象）
 * @param {String} rsaPrivateKey - RSA私钥（Base64编码）
 * @returns {String} 解密后的原始数据
 */
export function hybridDecrypt(encryptedContent, rsaPrivateKey) {
  if (!encryptedContent) {
    return encryptedContent
  }

  if (!JSEncrypt) {
    console.error('jsencrypt 未安装，无法使用混合解密')
    throw new Error('jsencrypt 未安装，无法使用混合解密')
  }

  if (!CryptoJS) {
    console.error('crypto-js 未安装，无法使用混合解密')
    throw new Error('crypto-js 未安装，无法使用混合解密')
  }

  try {
    // 第一步：解析加密数据（可能是JSON字符串或对象）
    let encryptedObj
    if (typeof encryptedContent === 'string') {
      encryptedObj = JSON.parse(encryptedContent)
    } else {
      encryptedObj = encryptedContent
    }

    // 验证加密对象结构
    if (!encryptedObj || !encryptedObj.encryptedKey || !encryptedObj.encryptedData) {
      throw new Error('加密数据格式错误，缺少 encryptedKey 或 encryptedData')
    }

    // 第二步：使用RSA私钥解密AES密钥（得到Base64字符串）
    console.error('🔐 [混合解密] 开始RSA解密AES密钥', {
      encryptedKeyLength: encryptedObj.encryptedKey ? encryptedObj.encryptedKey.length : 0,
      encryptedKeyPreview: encryptedObj.encryptedKey ? encryptedObj.encryptedKey.substring(0, 50) + '...' : 'null',
      hasPrivateKey: !!rsaPrivateKey,
      privateKeyLength: rsaPrivateKey ? rsaPrivateKey.length : 0,
      privateKeyPreview: rsaPrivateKey ? rsaPrivateKey.substring(0, 50) + '...' : 'null'
    })
    
    const aesKeyBase64 = rsaDecrypt(encryptedObj.encryptedKey, rsaPrivateKey)
    
    console.error('🔐 [混合解密] RSA解密AES密钥完成', {
      success: !!aesKeyBase64,
      aesKeyBase64Length: aesKeyBase64 ? aesKeyBase64.length : 0,
      aesKeyBase64Preview: aesKeyBase64 ? aesKeyBase64.substring(0, 30) : 'null',
      expectedLength: 24,
      fullAesKeyBase64: aesKeyBase64 // 临时输出完整密钥用于调试
    })
    
    // 验证AES密钥Base64格式是否正确
    if (aesKeyBase64) {
      const base64Regex = /^[A-Za-z0-9+/=]+$/
      if (!base64Regex.test(aesKeyBase64)) {
        console.error('❌ AES密钥Base64格式不正确，包含非法字符:', aesKeyBase64)
        throw new Error('AES密钥Base64格式不正确')
      }
      
      // 尝试Base64解码验证
      try {
        const testDecode = CryptoJS.enc.Base64.parse(aesKeyBase64)
        console.error('🔐 [验证] AES密钥Base64解码测试:', {
          decodedBytes: testDecode.sigBytes,
          expectedBytes: 16,
          isValid: testDecode.sigBytes === 16,
          hexPreview: CryptoJS.enc.Hex.stringify(testDecode).substring(0, 32)
        })
      } catch (e) {
        console.error('❌ AES密钥Base64解码失败:', e)
      }
    }
    
    // 验证解密结果
    if (!aesKeyBase64 || typeof aesKeyBase64 !== 'string') {
      console.error('RSA解密AES密钥失败:', {
        result: aesKeyBase64,
        type: typeof aesKeyBase64,
        isFalse: aesKeyBase64 === false,
        isNull: aesKeyBase64 === null
      })
      throw new Error('RSA解密AES密钥失败，返回值为: ' + typeof aesKeyBase64 + ', 值: ' + aesKeyBase64)
    }
    
    // 验证AES密钥Base64长度（16字节的密钥Base64编码后应该是24个字符，但可能有填充）
    const expectedLength = 24 // Base64编码的16字节密钥应该是24个字符（无填充）或22-24字符（可能有填充）
    const isValidLength = aesKeyBase64.length >= 20 && aesKeyBase64.length <= 24
    
    console.log('混合解密步骤1: RSA解密AES密钥结果', {
      base64Length: aesKeyBase64.length,
      type: typeof aesKeyBase64,
      preview: aesKeyBase64.substring(0, 30),
      expectedLength: expectedLength,
      isValidLength: isValidLength,
      warning: !isValidLength ? '警告: AES密钥长度异常，可能导致解密失败' : null
    })
    
    if (!isValidLength) {
      console.error('AES密钥长度异常:', {
        actualLength: aesKeyBase64.length,
        expectedLength: expectedLength,
        keyPreview: aesKeyBase64.substring(0, 50),
        hint: '这通常意味着RSA解密失败，返回了错误的数据'
      })
    }
    
    // 第三步：将Base64密钥解码为WordArray用于AES解密
    let aesKeyWordArray
    try {
      // 清理可能的空白字符
      const cleanKey = String(aesKeyBase64).trim()
      aesKeyWordArray = CryptoJS.enc.Base64.parse(cleanKey)
      console.debug('混合解密步骤2: AES密钥Base64解码成功', {
        bytes: aesKeyWordArray.sigBytes,
        expectedBytes: 16,
        isValidLength: aesKeyWordArray.sigBytes === 16,
        keyHex: CryptoJS.enc.Hex.stringify(aesKeyWordArray).substring(0, 32)
      })
      
      // 验证AES密钥长度（应该是16字节 = 128位）
      if (aesKeyWordArray.sigBytes !== 16) {
        console.error('错误: AES密钥长度不是16字节，当前长度:', aesKeyWordArray.sigBytes)
        console.error('AES密钥Base64值:', aesKeyBase64)
        console.error('AES密钥Hex值:', CryptoJS.enc.Hex.stringify(aesKeyWordArray))
        throw new Error('AES密钥长度不正确，期望16字节，实际' + aesKeyWordArray.sigBytes + '字节')
      }
    } catch (parseError) {
      console.error('AES密钥Base64解析失败:', parseError)
      console.error('AES密钥Base64值:', aesKeyBase64)
      console.error('AES密钥Base64长度:', aesKeyBase64.length)
      throw new Error('AES密钥Base64解析失败: ' + parseError.message)
    }
    
    // 第四步：恢复特殊字符
    if (typeof encryptedObj.encryptedData !== 'string') {
      throw new Error('encryptedData 不是字符串类型: ' + typeof encryptedObj.encryptedData)
    }
    let encryptedData = String(encryptedObj.encryptedData).replace(/~SLASH~/g, '/').replace(/~SUB~/g, '+')
    
    // 第五步：Base64解码加密数据
    const encryptedDataBytes = CryptoJS.enc.Base64.parse(encryptedData)
    
    // 第六步：创建加密对象
    const encrypted = CryptoJS.lib.CipherParams.create({
      ciphertext: encryptedDataBytes
    })
    
    // 第七步：使用AES密钥解密数据（直接使用WordArray）
    console.debug('混合解密步骤2.5: 开始AES解密，加密数据长度:', encryptedDataBytes.sigBytes, 'AES密钥字节数:', aesKeyWordArray.sigBytes)
    
    const decrypted = CryptoJS.AES.decrypt(
      encrypted,
      aesKeyWordArray,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )
    
    console.debug('混合解密步骤2.6: AES解密完成，解密后WordArray字节数:', decrypted.sigBytes)
    
    // 检查解密结果是否有效
    if (!decrypted || decrypted.sigBytes === 0) {
      throw new Error('AES解密失败：解密结果为空或无效')
    }
    
    // 第八步：转换为UTF-8字符串
    let decryptedData
    try {
      // 先检查解密后的数据是否可能是Base64编码的（后端可能返回了双重加密）
      const decryptedHex = CryptoJS.enc.Hex.stringify(decrypted)
      console.error('🔍 [调试] 解密后数据Hex预览（前64字符）:', decryptedHex.substring(0, 64))
      console.error('🔍 [调试] 解密后数据字节数:', decrypted.sigBytes)
      
      // 检查前几个字节是否可能是JSON的起始字符
      const firstBytes = decryptedHex.substring(0, 16)
      console.error('🔍 [调试] 前8个字节（Hex）:', firstBytes)
      
      // 尝试转换为UTF-8
      decryptedData = decrypted.toString(CryptoJS.enc.Utf8)
      console.error('✅ [成功] AES解密并转换为UTF-8成功，数据长度:', decryptedData.length, '预览:', decryptedData.substring(0, 50))
      
      // 验证解密后的数据是否看起来像JSON（应该以 { 或 [ 开头）
      if (decryptedData && decryptedData.length > 0) {
        const trimmed = decryptedData.trim()
        if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
          console.error('⚠️ [警告] 解密后的数据不是以 { 或 [ 开头，可能解密失败')
          console.error('数据预览:', trimmed.substring(0, 100))
        } else {
          console.error('✅ [验证] 解密后的数据格式正确（以JSON开头）')
        }
      }
    } catch (utf8Error) {
      // 如果UTF-8转换失败，说明AES密钥可能不正确
      console.error('UTF-8转换失败，AES密钥可能不正确:', utf8Error)
      
      // 输出更多调试信息
      try {
        // 尝试Base64编码查看原始数据
        const base64Data = decrypted.toString(CryptoJS.enc.Base64)
        console.error('解密后的Base64数据（前100字符）:', base64Data.substring(0, 100))
        console.error('解密后的Base64数据长度:', base64Data.length)
        
        // 尝试Latin1编码查看
        const latin1Data = decrypted.toString(CryptoJS.enc.Latin1)
        console.error('解密后的Latin1数据预览（前100字符）:', latin1Data.substring(0, 100))
        
        // 检查数据的前几个字节
        const hexData = CryptoJS.enc.Hex.stringify(decrypted)
        console.error('解密后的Hex数据（前64字符）:', hexData.substring(0, 64))
        
        // 检查是否可能是Base64编码的数据（需要再次解密）
        const firstBytes = hexData.substring(0, 8)
        console.error('解密后数据的前4个字节（Hex）:', firstBytes)
        
      } catch (e) {
        console.error('无法获取解密后的数据详情:', e)
      }
      
      // 检查RSA解密AES密钥的结果
      console.error('RSA解密AES密钥结果检查:', {
        aesKeyBase64Length: aesKeyBase64.length,
        aesKeyBase64Preview: aesKeyBase64.substring(0, 30),
        expectedLength: 24,
        isCorrectLength: aesKeyBase64.length === 24
      })
      
      throw new Error('AES解密失败：解密后的数据不是有效的UTF-8格式。这可能是因为RSA解密AES密钥失败，导致使用了错误的AES密钥。请检查RSA私钥是否正确配置。错误详情: ' + utf8Error.message)
    }
    
    // 验证解密后的数据是否为空
    if (!decryptedData || decryptedData.length === 0) {
      throw new Error('AES解密后数据为空')
    }
    
    return decryptedData
  } catch (error) {
    console.error('混合解密失败:', error)
    console.error('混合解密失败详情:', {
      errorMessage: error.message,
      errorStack: error.stack
    })
    throw new Error('混合解密失败: ' + error.message)
  }
}

export default {
  CryptoType,
  aesEncrypt,
  aesDecrypt,
  rsaEncrypt,
  rsaDecrypt,
  encrypt,
  decrypt,
  generateAESKey,
  hybridEncrypt,
  hybridDecrypt
}
